import {
    bestResolutionForBudget,
    candleBudgetForSeries,
    clampResolutionToBudget,
} from '../components/charts/chartResolution';
import type { EventModel } from './model/types';

const DAY_MS = 24 * 60 * 60 * 1000;
const FALLBACK_SPAN_MS = 30 * DAY_MS;

export const NOW_BUCKET_MS = 5 * 60 * 1000;

export function bucketedNow(nowMs: number = Date.now()): number {
    return Math.ceil(nowMs / NOW_BUCKET_MS) * NOW_BUCKET_MS;
}

function earliestQuoteStart(event: EventModel): number | null {
    let earliest: number | null = null;
    for (const outcome of event.outcomes) {
        for (const quote of outcome.quotes) {
            const t = quote.startDate?.getTime();
            if (t !== undefined && (earliest === null || t < earliest)) {
                earliest = t;
            }
        }
    }
    return earliest;
}

export function eventBounds(event: EventModel, nowMs: number): [Date, Date] {
    const endMs = event.endDate?.getTime() ?? null;
    const end =
        event.status === 'open' || endMs === null
            ? nowMs
            : Math.min(endMs, nowMs);
    const startMs =
        event.startDate?.getTime() ??
        earliestQuoteStart(event) ??
        end - FALLBACK_SPAN_MS;
    const start = startMs < end ? startMs : end - FALLBACK_SPAN_MS;
    return [new Date(start), new Date(end)];
}

export function pickResolution(
    range: readonly [Date, Date],
    seriesCount: number,
    available: readonly number[],
    requested: number | null = null
): number {
    const rangeMinutes = (range[1].getTime() - range[0].getTime()) / 60000;
    const budget = candleBudgetForSeries(seriesCount);
    if (requested === null) {
        return bestResolutionForBudget(rangeMinutes, [...available], budget);
    }
    return clampResolutionToBudget(
        rangeMinutes,
        requested,
        [...available],
        budget
    );
}

export function parseRange(
    from: string | null,
    to: string | null
): [Date, Date] | null {
    if (!from || !to) return null;
    const a = new Date(from);
    const b = new Date(to);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    return a < b ? [a, b] : null;
}
